/**
 * <product-card> — colour swatches on a listing card.
 *
 * Every card's swatches (style cards and colourway cards alike) are buttons in the shared selector
 * (assets/selector.js), each carrying what the card needs to switch to that colour without another
 * request: its image and srcset, its price (already formatted by the server), its colour name, its
 * quiet label ("Exclusive", or empty for none; absent when the listing shows no labels), and either
 * its variant id or, for a sibling colourway that is its own product, that product's address.
 *
 * Choosing one keeps the shopper on the listing and switches the card in place — image, price,
 * colour name, label (labels are per colour, U17) and every link on the card. A variant swatch sets the links' ?variant= parameter,
 * replacing any already there and keeping the rest of each address (the collection path among it),
 * so clicking the card afterwards opens the product page on the chosen colour (SAVETTE-64).
 *
 * The selector owns the pressed state, the roving focus and Enter/Space; focus stays on the swatch.
 * The card listens for the click and the Enter/Space themselves rather than the selector's change
 * event, which a press on the swatch already pressed does not send: the selector (inside the card)
 * handles them first, and the card then shows whichever swatch is pressed. Showing is idempotent,
 * so pressing the current swatch again always brings the card back in line with it.
 *
 * Without JavaScript the swatch buttons do nothing (accepted); the card's links open its own colour.
 *
 * On a listing the card's image is a gallery of its colour's images (SAVETTE-70,
 * snippets/product-card-slides.liquid): a scroll-snap strip that the shopper swipes natively, so
 * nothing here drags anything. The card only steps the strip for the arrows (one image a click,
 * two quick clicks two images; the CSS decides smooth or instant, honouring reduced motion), hides
 * each arrow at its end of the strip (moving focus to the other one rather than losing it), and
 * loads images ahead of the shopper: each card's second image once the card comes within a screen
 * of view (one observer for the whole page, at low priority), the rest once the strip moves. A
 * swatch that switches the card to another colour drops the old colour's images at once, keeping
 * the swapped first image, and fetches the new colour's from the server
 * (sections/product-card-media.liquid through the Section Rendering API, one request per colour
 * per page; a failed one is tried again when the swatch is pressed again). Nothing runs at load
 * beyond the listeners and the observer.
 */
const MEDIA_SECTION = 'product-card-media';
/** One request per colour address for the life of the page, however many cards ask for it. */
const colourSlides = new Map();

/**
 * A lazy image clipped by the strip does not load until it is scrolled into it, so a swipe would
 * show an empty frame. Each card's second image is loaded instead as the card nears the screen —
 * at low priority, behind everything the page needs first; the rest stay lazy.
 */
const nearView =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            nearView.unobserve(entry.target);
            const card = entry.target.closest('product-card');
            if (card) {
              card.near = true;
              card.loadSecond();
            }
          });
        },
        { rootMargin: '100% 0px' }
      )
    : null;

class ProductCard extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.strip = this.querySelector('.product-card__media--gallery [data-card-strip]');
    if (!this.strip) return;
    this.strip.addEventListener('scroll', this.onScroll, { passive: true });
    // the shopper taking the strip in hand (a touch, a trackpad) lets go of any arrow's target
    this.strip.addEventListener('pointerdown', this.dropTarget, { passive: true });
    this.strip.addEventListener('wheel', this.dropTarget, { passive: true });
    nearView?.observe(this.strip);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.strip?.removeEventListener('scroll', this.onScroll);
    this.strip?.removeEventListener('pointerdown', this.dropTarget);
    this.strip?.removeEventListener('wheel', this.dropTarget);
    if (this.strip) nearView?.unobserve(this.strip);
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  onKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') this.onActivate(event);
  };

  /** An arrow steps the strip (its own click, keyboard included); anything else may be a swatch. */
  onClick = (event) => {
    const arrow = event.target.closest('[data-card-step]');
    if (arrow && this.contains(arrow)) {
      this.step(Number(arrow.dataset.cardStep));
      return;
    }
    this.onActivate(event);
  };

  onActivate = (event) => {
    const swatch = event.target.closest('.selector--swatch .selector__item:not([data-more])');
    if (!swatch || !this.contains(swatch) || swatch.getAttribute('aria-pressed') !== 'true') return;
    this.show(swatch);
  };

  show(swatch) {
    const { image: src, srcset, imageAlt, price, colour, label, variantId, url } = swatch.dataset;

    const image = this.querySelector('.product-card__image');
    if (image && src) {
      // the srcset goes first: a stale one would win over the new src
      if (srcset) image.srcset = srcset;
      else image.removeAttribute('srcset');
      image.src = src;
      if (imageAlt) image.alt = imageAlt;
    }

    const priceEl = this.querySelector('[data-card-price]');
    if (priceEl && price) priceEl.textContent = price;

    const colourEl = this.querySelector('[data-card-colour]');
    if (colourEl && colour) colourEl.textContent = colour;

    // The label over the image, and its copy in the title link that screen readers hear; a colour
    // without a label hides both.
    if (label !== undefined) {
      const labelEl = this.querySelector('[data-card-label]');
      if (labelEl) {
        labelEl.textContent = label;
        labelEl.hidden = !label;
      }
      const spoken = this.querySelector('[data-card-label-spoken]');
      if (spoken) {
        const text = spoken.querySelector('[data-card-label-text]');
        if (text) text.textContent = label;
        spoken.hidden = !label;
      }
    }

    const shownColour = this.strip?.getAttribute('href');
    this.querySelectorAll('[data-card-link]').forEach((link) => {
      const href = variantId ? withVariant(link.getAttribute('href'), variantId) : url;
      if (href) link.setAttribute('href', href);
    });

    // The strip is one of the card's links, so its address is now the chosen colour's. A colour
    // whose images failed to arrive is asked for again when its swatch is pressed again.
    const colourHref = this.strip?.getAttribute('href');
    if (colourHref && (colourHref !== shownColour || colourHref === this.failedColour)) {
      this.showColour(colourHref, Boolean(image && src));
    }
  }

  /**
   * The chosen colour's images: the old colour's extra images go at once, and the colour's own
   * arrive from the server. The first image is kept when the swatch already swapped it; a swatch
   * that carried no image has the server's first image put in its place.
   */
  async showColour(href, firstSwapped) {
    const strip = this.strip;
    // only the latest call writes: a quick B → A → B must not leave B's images in twice
    const token = (this.slidesToken = {});
    this.failedColour = null;
    this.targetIndex = null;
    this.setSlides([]);
    strip.style.scrollBehavior = 'auto';
    strip.scrollLeft = 0;
    strip.style.scrollBehavior = '';
    this.refresh();

    const address = sectionAddress(href);
    if (!address) return;
    let request = colourSlides.get(address);
    if (!request) {
      request = fetchSlides(address);
      colourSlides.set(address, request);
    }
    const slides = await request;
    if (this.slidesToken !== token || !this.isConnected) return;
    if (!slides) {
      this.failedColour = href;
      return;
    }

    if (!firstSwapped && slides[0]) {
      const first = strip.querySelector('.product-card__image');
      const replacement = document.importNode(slides[0], false);
      if (first) {
        const sizes = first.getAttribute('sizes');
        if (sizes) replacement.setAttribute('sizes', sizes);
        replacement.loading = first.loading;
        first.replaceWith(replacement);
      }
    }
    this.setSlides(slides.slice(1));
    this.refresh();
  }

  /** Every image after the first replaced by these (inert ones from the server), lazy. */
  setSlides(slides) {
    const strip = this.strip;
    strip.querySelectorAll('.product-card__slide:not(.product-card__image)').forEach((slide) => slide.remove());
    const sizes = strip.querySelector('.product-card__image')?.getAttribute('sizes');
    slides.forEach((slide) => {
      const image = document.importNode(slide, false);
      if (sizes) image.setAttribute('sizes', sizes);
      image.loading = 'lazy';
      strip.append(image);
    });
    // the new images wait for the same signs of interest the first colour's did
    this.allWarm = false;
    if (this.near) this.loadSecond();
  }

  /**
   * One image along (direction 1) or back (-1). Steps add up while the strip is still moving, so
   * two quick clicks move two images rather than both aiming at the one next to where it started.
   */
  step(direction) {
    const strip = this.strip;
    const width = strip?.clientWidth;
    if (!width) return;
    this.warmAll();
    const last = strip.querySelectorAll('.product-card__slide').length - 1;
    const from = this.targetIndex ?? Math.round(Math.abs(strip.scrollLeft) / width);
    const index = Math.min(Math.max(from + direction, 0), Math.max(last, 0));
    this.targetIndex = index;
    const sign = getComputedStyle(strip).direction === 'rtl' ? -1 : 1;
    strip.scrollTo({ left: index * width * sign });
  }

  dropTarget = () => {
    this.targetIndex = null;
  };

  onScroll = (event) => {
    // a real move means the shopper is looking through the images; a refresh() dispatch is not one
    if (event.isTrusted) this.warmAll();
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.updateArrows();
    });
  };

  /** Arrows and indicator back in line after the strip's images changed without it scrolling. */
  refresh() {
    this.updateArrows();
    // the indicator (assets/scroll-indicator.js) re-measures on scroll; its box has not changed
    this.strip.dispatchEvent(new Event('scroll'));
  }

  updateArrows() {
    const strip = this.strip;
    const prev = this.querySelector('[data-card-step="-1"]');
    const next = this.querySelector('[data-card-step="1"]');
    if (!strip || !prev || !next) return;

    const width = strip.clientWidth;
    const travel = strip.scrollWidth - width;
    const at = Math.abs(strip.scrollLeft);
    const hidePrev = at < 1;
    const hideNext = at >= travel - 1;
    // A step's target is let go once the strip reaches it. Not on scrollend: a click that redirects
    // a smooth scroll still under way ends that scroll, which would drop the target mid-run.
    if (this.targetIndex != null && Math.abs(this.targetIndex * width - at) < 2) this.targetIndex = null;
    const focused = document.activeElement;
    prev.hidden = hidePrev;
    next.hidden = hideNext;
    // an arrow that reaches its end hands focus to the other rather than dropping it on the page
    if (focused === prev && hidePrev && !hideNext) next.focus({ preventScroll: true });
    if (focused === next && hideNext && !hidePrev) prev.focus({ preventScroll: true });
  }

  /** The second image, once the card nears the screen: the likeliest next look, at low priority. */
  loadSecond() {
    const second = this.strip?.querySelectorAll('.product-card__slide')[1];
    if (!second || second.loading === 'eager') return;
    second.fetchPriority = 'low';
    second.loading = 'eager';
  }

  /** Every image, once the shopper moves the strip. */
  warmAll() {
    if (this.allWarm) return;
    this.allWarm = true;
    this.strip?.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.loading = 'eager';
    });
  }
}

/** The colour's address asking for the card images section alone, or null off this site. */
function sectionAddress(href) {
  const target = new URL(href, window.location.origin);
  if (target.origin !== window.location.origin) return null;
  target.searchParams.set('section_id', MEDIA_SECTION);
  return `${target.pathname}${target.search}`;
}

/** The colour's images from the server, as inert <img> elements; null when the request fails. */
async function fetchSlides(address) {
  try {
    const response = await fetch(address);
    if (!response.ok) throw new Error(`${response.status}`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return [...doc.querySelectorAll('[data-card-slides] .product-card__slide')];
  } catch {
    // the card keeps its one image; a later press of this colour's swatch tries again
    colourSlides.delete(address);
    return null;
  }
}

/** The address with its ?variant= set to this id, replacing any existing one; the rest is kept. */
function withVariant(href, variantId) {
  if (!href) return null;
  const target = new URL(href, window.location.origin);
  // An address joined with a second ? (…&pr_seq=uniform?variant=123) carries its variant inside
  // another parameter's value; take it back out rather than leave that value polluted.
  const params = new URLSearchParams();
  for (const [key, value] of target.searchParams) {
    if (key !== 'variant') params.append(key, value.replace(/\?variant=[^&]*$/, ''));
  }
  params.set('variant', variantId);
  target.search = params.toString();
  return target.origin === window.location.origin ? `${target.pathname}${target.search}${target.hash}` : target.href;
}

if (!customElements.get('product-card')) customElements.define('product-card', ProductCard);
